import usePessoas from "features/pessoas/hooks/usePessoas";
import { useState } from "react";
import SelectBaseAntd, { SelectBaseAntdProps } from "../SelectBaseAntd";
import { TipoEntidadeEnum } from "resources/enums/conta";
import { useEffect, useCallback, useRef } from "react";
import { usePrevious } from "@chakra-ui/hooks";
import { useDebounce } from "features/shared/useDebouce";
import { diff } from "deep-object-diff";
import { useCurrentTenant } from "features/auth/tenant.store";
import { PessoaTipo, PessoaItem } from "features/pessoas/typings";
import ModalCadastroPessoa from "./ModalCadastroPessoa";
import { Box } from "@chakra-ui/react";
import { Button } from "@chakra-ui/react";
import { PaginateResult } from "core/resources/types/shared";
import { TipoUnidadePessoaEnum } from "core/features/unidades/typings/unidade";

export interface SelectPessoasProps<T = any> extends SelectBaseAntdProps<T> {
  value: any;
  unidadeId?: string | string[];
  tipoPessoa?: PessoaTipo;
  tenantId?: string | null;
  tipoEntidades?: TipoEntidadeEnum[];
  tipoUnidadePessoa?: TipoUnidadePessoaEnum[];
  isInvalid?: boolean;
  semDocumentoDuplicado?: boolean;
  somenteComDocumento?: boolean;
  filteredOptions?: string[];
  incluirPessoasComUnidade?: boolean;
  prependOptions?: {
    label: string;
    value: string;
  }[];
}

function SelectPessoas({
  value,
  tenantId,
  unidadeId,
  onChange,
  isInvalid,
  tipoEntidades,
  tipoUnidadePessoa,
  filteredOptions,
  tipoPessoa,
  incluirPessoasComUnidade,
  prependOptions,
  semDocumentoDuplicado,
  somenteComDocumento,
  ...rest
}: SelectPessoasProps) {
  const onChangeBuffer = useRef<
    | {
        id: string;
        nome: string;
      }
    | undefined
  >(undefined);
  const { tenant } = useCurrentTenant();

  const [isOpenModal, setIsOpenModal] = useState(false);

  const [search, setSearch] = useState<string>();

  const debouncedSearchTerm = useDebounce(search, 500);

  const tipoEntidadeValue = usePrevious(tipoEntidades);

  const onFinishFetching = useCallback(
    (_data: PaginateResult<PessoaItem>) => {
      if (
        _data &&
        Array.isArray(_data.items) &&
        !_data.items.length &&
        value &&
        typeof onChange === "function"
      ) {
        if (rest.mode === "multiple" || rest.mode === "tags") {
          onChange([], {} as any);
        } else {
          onChange("", {} as any);
        }
      } else if (onChangeBuffer.current) {
        const { id, nome } = onChangeBuffer.current;
        onChange?.(id, {
          value: id,
          label: nome,
        });
        onChangeBuffer.current = undefined;
      }
    },
    [onChange, rest.mode, value],
  );

  const { data, isFetching, refetch, isLoading } = usePessoas(
    tenantId ?? tenant?.id,
    {
      page: 1,
      limit: 20,
      search: debouncedSearchTerm,
      unidadeId,
      tipoPessoa,
      tipoEntidades,
      tipoUnidadePessoa,
      pessoaId: debouncedSearchTerm ? undefined : value,
      incluirPessoasComUnidade: incluirPessoasComUnidade ? "1" : undefined,
      semDocumentoDuplicado: semDocumentoDuplicado ? "1" : "0",
      somenteComDocumento: somenteComDocumento ? "1" : "0",
    },
    onFinishFetching,
  );

  useEffect(() => {
    if (!tipoEntidades || !tipoEntidadeValue) {
      return;
    }

    const isDiff = Object.keys(diff(tipoEntidades, tipoEntidadeValue)).length;

    if (typeof onChange === "function" && isDiff && !isFetching) {
      onChange(undefined, {} as any);
    }
  }, [tipoEntidades, onChange, tipoEntidadeValue, isFetching]);

  const getOptions: () => { label: string; value: string }[] = () => {
    const options = [];

    let _items =
      data?.items.filter((item) =>
        filteredOptions ? !filteredOptions.includes(item.id) : true,
      ) ?? [];

    if (prependOptions) {
      options.push(...prependOptions);

      _items = _items?.filter(
        (i) => !prependOptions.find((o) => o.value === i.id),
      );
    }

    if (data?.items) {
      options.push(
        ..._items?.map((item) => ({
          label: item.nome,
          value: item.id,
        })),
      );
    }

    return options;
  };
  return (
    <>
      {isOpenModal ? (
        <ModalCadastroPessoa
          isOpen={isOpenModal}
          onClose={() => setIsOpenModal(false)}
          onSaved={({ id, nome }) => {
            onChangeBuffer.current = { id, nome };
            refetch();
          }}
        />
      ) : null}
      <SelectBaseAntd
        loading={isLoading}
        isInvalid={isInvalid}
        size="large"
        showSearch
        placeholder="Selecione..."
        searchValue={search}
        onSearch={setSearch}
        onChange={onChange}
        value={value}
        filterOption={() => true}
        options={getOptions()}
        allowClear
        {...rest}
        notFoundContent={
          !isFetching ? (
            <Box>
              Nenhuma pessoa encontrada para essa busca
              <Button
                isLoading={false}
                leftIcon={<i className="mdi mdi-plus" />}
                size="xs"
                colorScheme="orange"
                onClick={() => setIsOpenModal(true)}
              >
                cadastrar nova
              </Button>
            </Box>
          ) : (
            <Box>Carregando...</Box>
          )
        }
      />
    </>
  );
}

export default SelectPessoas;
